import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  Icon,
  Pane,
  PaneFooter,
  RepeatableField,
  Select,
  TextField
} from '@folio/stripes/components';
import { FormattedMessage, useIntl } from 'react-intl';
import stripesFinalForm from '@folio/stripes/final-form';
import { FormSpy } from 'react-final-form';
import AddToCategory from './components/AddToCategory';
import AdministrativeData from './components/AdministrativeData';
import AuthorizedUsers from './components/AuthorizedUsers';
import ReportVisualization from './components/ReportVisualization';
import AdvancedSettings from './components/AdvancedSettings';

function SaveNewReportForm({
  pristine,
  submitting,
  handleSubmit,
  form,
  showQueryBuilderPane,
  setShowQueryBuilderPane,
  showSavePane,
  setShowSavePane,
  handleClose,
  categories,
  invalid,
  hasValidationErrors,
  canCloseRef,
  saveMode = 'new',
  queryDimensions,
}) {
  const isUpdateMode = saveMode === 'update';
  const renderPaneFooter = () => {
    return (
      <PaneFooter
        renderEnd={
          <Button
            buttonStyle="default"
            // disabled={pristine || submitting}
            id="save-new-report-pane-cancel-btn"
            marginBottom0
            onClick={() => {
              setShowSavePane(false);
              setShowQueryBuilderPane(true);
            }}
          >
            <FormattedMessage
              id="ui-reports.newReport.saveNewReportForm.cancel.btn"
              defaultMessage="Cancel"
            />
          </Button>
        }
        renderStart={
          <>
            <Button
              buttonStyle="primary"
              disabled={submitting || hasValidationErrors}
              id="save-new-report-pane-save-btn"
              marginBottom0
              type="submit"
              onClick={async (e) => {
                e.preventDefault();
                await handleSubmit();
                if (!canCloseRef?.current) return;
                if (!isUpdateMode) {
                  setShowSavePane(false);
                  setShowQueryBuilderPane(true);
                }
              }}
            >
              {isUpdateMode ? (
                <FormattedMessage id="ui-reports.saveReport.updateBtn" defaultMessage="Update" />
              ) : (
                <FormattedMessage id="ui-reports.newReport.saveNewReportForm.saveBtn" defaultMessage="Save" />
              )}
            </Button>
            <Button
              buttonStyle="primary"
              id="clickable-closeAndSaveBtn-reports"
              marginBottom0
              type="submit"
              onClick={async (e) => {
                e.preventDefault();
                await handleSubmit();
                if (!canCloseRef?.current) return;
                form.reset();
                handleClose();
              }}
              disabled={submitting || hasValidationErrors}
            >
              {isUpdateMode ? (
                <FormattedMessage id="ui-reports.saveReport.updateAndCloseBtn" defaultMessage="Update & Close" />
              ) : (
                <FormattedMessage id="ui-reports.newReport.reportPreviewPane.closeAndSaveBtn" defaultMessage="Save & Close" />
              )}
            </Button>
          </>
        }
      />
    );
  };

  return (
    <>
      {showSavePane ? (
        <Pane
          appIcon={<Icon icon="save" size="large" />}
          defaultWidth="30%"
          fluidContentWidth
          // firstMenu={renderFirstMenu()}
          // lastMenu={<NewReportActions />}
          footer={renderPaneFooter()}
          id="pane-report-save"
          centeredContent
          paneTitle={
            isUpdateMode ? (
              <FormattedMessage
                id="ui-reports.saveReport.paneTitle.update"
                defaultMessage="Update report"
              />
            ) : (
              <FormattedMessage
                id="ui-reports.newReport.saveReportPane.paneTitle"
                defaultMessage="Save the report"
              />
            )
          }
        >
          <form id="form-save-new-report" onSubmit={handleSubmit}>
            <AdministrativeData />
            <FormSpy subscription={{ values: true }}>
              {({ values }) => (
                <AuthorizedUsers visibility={values.visibility} />
              )}
            </FormSpy>
            <AddToCategory categories={categories} />
            <ReportVisualization queryDimensions={queryDimensions} />
            <AdvancedSettings />
          </form>
        </Pane>
      ) : (
        <div />
      )}
    </>
  );
}

SaveNewReportForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool,
  submitting: PropTypes.bool,
  form: PropTypes.object.isRequired,
  showQueryBuilderPane: PropTypes.bool.isRequired,
  setShowQueryBuilderPane: PropTypes.func.isRequired,
  showSavePane: PropTypes.bool.isRequired,
  setShowSavePane: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  categories: PropTypes.arrayOf(PropTypes.object),
  invalid: PropTypes.bool,
  hasValidationErrors: PropTypes.bool,
  saveMode: PropTypes.string,
  queryDimensions: PropTypes.arrayOf(PropTypes.string),
};

export default stripesFinalForm({
  mutators: {},
  validateOnBlur: true,
  navigationCheck: true,
  subscription: {
    values: true,
    invalid: true,
    pristine: true,
    submitting: true,
    hasValidationErrors: true
  }
})(SaveNewReportForm);
